import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { IInformationWidgetField, InformationWidgetFieldTypes } from '.';
import { formatCurrencyNumber, formatDate, formatDateTime } from 'src/helpers/formatting-utils';
import { isEmpty } from 'src/helpers/validation-utils';

interface IInformationDisplayProps {
  fields: IInformationWidgetField[];
  data: Record<string, any>;
  loading?: boolean;
  variant?: 'property-grid' | 'modern-details' | 'stat-cards' | 'data-table' | 'inline' | 'definition-list';
}

const useStyles = makeStyles()((theme) => ({
  container: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    boxShadow: theme.shadows[1],
  },
  headerAccent: {
    height: theme.spacing(0.5),
    background: theme.palette.primary.main,
  },

  propertyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1px',
    backgroundColor: theme.palette.divider,
  },
  propertyItem: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    }
  },
  propertyLabel: {
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  propertyValue: {
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
  },

  detailsContainer: {
    padding: theme.spacing(4),
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: theme.spacing(4),
  },
  detailGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  detailLabel: {
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.light,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  detailValue: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    minHeight: '1.5rem',
  },

  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  statCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    boxShadow: theme.shadows[1],
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[4],
    }
  },
  statLabel: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  statValue: {
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
  },

  dataTable: {
    width: '100%',
  },
  dataRow: {
    display: 'grid',
    gridTemplateColumns: '200px 1fr',
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: 'background-color 0.2s ease',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    }
  },
  dataLabel: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.grey[100],
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
    fontSize: theme.typography.body2.fontSize,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
  },
  dataValue: {
    padding: theme.spacing(2, 3),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
  },

  inlineContainer: {
    padding: theme.spacing(3),
  },
  inlineList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
  },
  inlineItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: '120px',
  },
  inlineLabel: {
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  inlineValue: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
  },

  definitionList: {
    padding: 0,
    margin: 0,
  },
  definitionTerm: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
    fontSize: theme.typography.body2.fontSize,
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2, 3, 1),
    margin: 0,
  },
  definitionDescription: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    fontSize: theme.typography.body1.fontSize,
    padding: theme.spacing(0, 3, 2),
    margin: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    }
  },

  currencyValue: {
    color: theme.palette.success.main,
    fontWeight: theme.typography.fontWeightBold,
    fontFamily: theme.typography.fontFamily + ', monospace',
  },
  booleanValue: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightBold,
  },
  booleanTrue: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  booleanFalse: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  emptyValue: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
  dateValue: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
  },

  loadingSkeleton: {
    height: '16px',
    backgroundColor: theme.palette.divider,
    borderRadius: '4px',
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  },
  loadingSkeletonWide: {
    width: '60%',
    height: '20px',
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.3,
    },
  },

  fullWidth: {
    gridColumn: '1 / -1',
  },
  twoThirds: {
    '@media (min-width: 900px)': {
      gridColumn: 'span 2',
    }
  }
}));


const ModernInformationDisplay: React.FC<IInformationDisplayProps> = ({
  fields,
  data,
  loading = false,
  variant = 'property-grid'
}) => {
  const { classes } = useStyles();

  const renderFieldValue = (field: IInformationWidgetField) => {
    if (isEmpty(data[field.name])) {
      return <span className={classes.emptyValue}>—</span>;
    }

    const value = data[field.name];
    
    switch (field.type) {
      case InformationWidgetFieldTypes.Currency:
        return (
          <span className={classes.currencyValue}>
            {formatCurrencyNumber(value)}
          </span>
        );
        
      case InformationWidgetFieldTypes.Date:
        return (
          <span className={classes.dateValue}>
            {formatDate(value)}
          </span>
        );
        
      case InformationWidgetFieldTypes.Datetime:
        return (
          <span className={classes.dateValue}>
            {formatDateTime(value)}
          </span>
        );
        
      case InformationWidgetFieldTypes.Boolean:
        return (
          <span className={`${classes.booleanValue} ${value ? classes.booleanTrue : classes.booleanFalse}`}>
            <span>{value ? '●' : '○'}</span>
            {value ? 'Yes' : 'No'}
          </span>
        );
        
      default:
        return <span>{value}</span>;
    }
  };

  const getWidthClass = (width?: "third" | 'two-third' | 'full') => {
    switch (width) {
      case 'full':
        return classes.fullWidth;
      case 'two-third':
        return classes.twoThirds;
      default:
        return '';
    }
  };

  const renderLoadingSkeleton = () => {
    return Array.from({ length: fields.length }).map((_, index) => (
      <div key={index} style={{ padding: '1rem' }}>
        <div className={classes.loadingSkeleton} style={{ width: '40%', marginBottom: '0.5rem' }}></div>
        <div className={`${classes.loadingSkeleton} ${classes.loadingSkeletonWide}`}></div>
      </div>
    ));
  };

  const renderPropertyGrid = () => (
    <div className={classes.container}>
      <div className={classes.headerAccent}></div>
      <div className={classes.propertyGrid}>
        {loading ? renderLoadingSkeleton() : fields.map((field) => (
          <div key={field.name} className={`${classes.propertyItem} ${getWidthClass(field.width)}`}>
            <div className={classes.propertyLabel}>{field.title}</div>
            <div className={classes.propertyValue}>{renderFieldValue(field)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderModernDetails = () => (
    <div className={classes.container}>
      <div className={classes.headerAccent}></div>
      <div className={classes.detailsContainer}>
        <div className={classes.detailsGrid}>
          {loading ? renderLoadingSkeleton() : fields.map((field) => (
            <div key={field.name} className={`${classes.detailGroup} ${getWidthClass(field.width)}`}>
              <div className={classes.detailLabel}>{field.title}</div>
              <div className={classes.detailValue}>{renderFieldValue(field)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStatCards = () => (
    <div className={classes.statGrid}>
      {loading ? renderLoadingSkeleton() : fields.map((field) => (
        <div key={field.name} className={classes.statCard}>
          <div className={classes.statLabel}>{field.title}</div>
          <div className={classes.statValue}>{renderFieldValue(field)}</div>
        </div>
      ))}
    </div>
  );

  const renderDataTable = () => (
    <div className={classes.container}>
      <div className={classes.headerAccent}></div>
      <div className={classes.dataTable}>
        {loading ? renderLoadingSkeleton() : fields.map((field) => (
          <div key={field.name} className={classes.dataRow}>
            <div className={classes.dataLabel}>{field.title}</div>
            <div className={classes.dataValue}>{renderFieldValue(field)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderInlineLayout = () => (
    <div className={classes.container}>
      <div className={classes.headerAccent}></div>
      <div className={classes.inlineContainer}>
        <div className={classes.inlineList}>
          {loading ? renderLoadingSkeleton() : fields.map((field) => (
            <div key={field.name} className={classes.inlineItem}>
              <div className={classes.inlineLabel}>{field.title}</div>
              <div className={classes.inlineValue}>{renderFieldValue(field)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDefinitionList = () => (
    <div className={classes.container}>
      <div className={classes.headerAccent}></div>
      <dl className={classes.definitionList}>
        {loading ? renderLoadingSkeleton() : fields.map((field) => (
          <React.Fragment key={field.name}>
            <dt className={classes.definitionTerm}>{field.title}</dt>
            <dd className={classes.definitionDescription}>{renderFieldValue(field)}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );

  const renderContent = () => {
    switch (variant) {
      case 'modern-details':
        return renderModernDetails();
      case 'stat-cards':
        return renderStatCards();
      case 'data-table':
        return renderDataTable();
      case 'inline':
        return renderInlineLayout();
      case 'definition-list':
        return renderDefinitionList();
      case 'property-grid':
      default:
        return renderPropertyGrid();
    }
  };

  return renderContent();
};

export default ModernInformationDisplay;